import { NavLink, Outlet, Navigate, useNavigate } from "react-router-dom";
import { LayoutDashboard, Send, Inbox, Settings, LogOut, MessageCircle, Loader2 } from "lucide-react";
import { useAuth } from "@/dashboard/AuthContext";

const NAV = [
  { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard, end: true, testid: "nav-dashboard" },
  { to: "/dashboard/send", label: "Send Messages", icon: Send, testid: "nav-send" },
  { to: "/dashboard/inbox", label: "Inbox", icon: Inbox, testid: "nav-inbox" },
  { to: "/dashboard/settings", label: "Settings", icon: Settings, testid: "nav-settings" },
];

const DashboardLayout = () => {
  const { user, loading, logout } = useAuth();
  const navigate = useNavigate();

  if (loading || user === null) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-brand-surface">
        <Loader2 className="w-6 h-6 animate-spin text-brand-blue" />
      </div>
    );
  }
  if (user === false) return <Navigate to="/dashboard/login" replace />;

  return (
    <div className="min-h-screen bg-brand-surface font-sans flex">
      {/* Sidebar */}
      <aside className="w-64 bg-white border-r border-brand-border hidden md:flex flex-col fixed inset-y-0 left-0">
        <div className="px-6 h-20 flex items-center gap-3 border-b border-brand-border">
          <div className="w-10 h-10 rounded-xl bg-brand-blue flex items-center justify-center">
            <MessageCircle className="w-5 h-5 text-white" strokeWidth={2} />
          </div>
          <div>
            <div className="font-display font-bold text-brand-ink leading-tight">WhatsApp</div>
            <div className="text-xs text-brand-muted">SNV Elite</div>
          </div>
        </div>
        <nav className="flex-1 px-3 py-6 space-y-1">
          {NAV.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.end}
              data-testid={n.testid}
              className={({ isActive }) =>
                `flex items-center gap-3 px-4 py-3 rounded-lg font-display font-medium text-sm transition-colors ${
                  isActive ? "bg-brand-blue text-white" : "text-brand-slate hover:bg-brand-surface"
                }`
              }
            >
              <n.icon className="w-4.5 h-4.5" strokeWidth={2} />
              {n.label}
            </NavLink>
          ))}
        </nav>
        <div className="px-3 py-4 border-t border-brand-border">
          <div className="px-4 py-2 mb-1">
            <div className="text-xs text-brand-muted">Signed in as</div>
            <div className="text-sm font-medium text-brand-ink truncate">{user.email}</div>
          </div>
          <button
            onClick={logout}
            data-testid="logout-btn"
            className="w-full flex items-center gap-3 px-4 py-3 rounded-lg font-display font-medium text-sm text-brand-red hover:bg-brand-red-light/40 transition-colors"
          >
            <LogOut className="w-4.5 h-4.5" strokeWidth={2} />
            Sign Out
          </button>
        </div>
      </aside>

      {/* Mobile top bar */}
      <div className="md:hidden fixed top-0 inset-x-0 bg-white border-b border-brand-border z-40 h-16 flex items-center justify-between px-4">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-lg bg-brand-blue flex items-center justify-center">
            <MessageCircle className="w-4 h-4 text-white" />
          </div>
          <span className="font-display font-bold">WhatsApp</span>
        </div>
        <button onClick={logout} className="text-brand-red"><LogOut className="w-5 h-5" /></button>
      </div>

      {/* Mobile bottom nav */}
      <nav className="md:hidden fixed bottom-0 inset-x-0 bg-white border-t border-brand-border z-40 flex justify-around py-2">
        {NAV.map((n) => (
          <NavLink
            key={n.to}
            to={n.to}
            end={n.end}
            className={({ isActive }) =>
              `flex flex-col items-center gap-0.5 px-3 py-1 text-xs ${isActive ? "text-brand-blue" : "text-brand-muted"}`
            }
          >
            <n.icon className="w-5 h-5" strokeWidth={2} />
            {n.label.split(" ")[0]}
          </NavLink>
        ))}
      </nav>

      {/* Content */}
      <main className="flex-1 md:ml-64 pt-20 md:pt-8 pb-24 md:pb-8 px-5 md:px-10">
        <Outlet />
      </main>
    </div>
  );
};

export default DashboardLayout;
